import { Button } from "@/components/ui/button.tsx";
import { useStatsigLogger } from "@/hooks/useStatsigLogger.ts";
import { cn } from "@/lib/utils.ts";
import { X } from "lucide-react";
import { motion } from "motion/react";
import { type PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "wouter";
import type { PositionConfig, TourStep } from "../types.ts";

const TailSvg = () => (
  <svg
    width="18"
    height="11"
    viewBox="0 0 18 11"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M7.23242 2.06055C8.20869 1.08444 9.79131 1.08444 10.7676 2.06055L16.207 7.5L1.79297 7.5L7.23242 2.06055Z"
      fill="#4B5563"
      stroke="#6B7280"
    />
    <rect
      x="18"
      y="11"
      width="18"
      height="4"
      rx="1.5"
      transform="rotate(-180 18 11)"
      fill="#4B5563"
    />
  </svg>
);

interface UseTourManagerProps {
  steps: TourStep[];
}

/**
 * Drops the `nux` param that opened the tour and keeps the rest of the query
 * string: the tour runs over the calendar, whose `date` and `view` params are
 * the deep link the user arrived on.
 */
const withoutNuxParam = (prev: URLSearchParams) => {
  const next = new URLSearchParams(prev);
  next.delete("nux");
  return next;
};

interface TourStepWrapperProps extends PropsWithChildren {
  closeTour: () => void;
  className?: string;
  anchorEl: Element | null;
  positionConfig?: PositionConfig;
  isTransitioning: boolean;
}

const noAnchorSpotlightRect = {
  left: window.innerWidth / 2,
  top: window.innerHeight / 2,
  width: 1,
  height: 1,
  borderRadius: 0,
};

const getCaretStyles = (positionType: string) => {
  const basePosition = positionType.split("-")[0];

  switch (basePosition) {
    case "top":
      return {
        className: "bottom-[-7px] left-1/2 -translate-x-1/2",
        rotation: 180,
      };
    case "bottom":
      return {
        className: "top-[-7px] left-1/2 -translate-x-1/2",
        rotation: 0,
      };
    case "left":
      return {
        className: "right-[-11px] top-1/2 -translate-y-1/2",
        rotation: 90,
      };
    case "right":
      return {
        className: "left-[-11px] top-1/2 -translate-y-1/2",
        rotation: -90,
      };
    default:
      return {
        className: "hidden",
        rotation: 0,
      };
  }
};

const TourStepWrapper = ({
  children,
  closeTour,
  className,
  anchorEl,
  positionConfig,
  isTransitioning,
}: TourStepWrapperProps) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  // Track which anchor the position was calculated for — when anchorEl changes,
  // positionReady automatically becomes false until calculatePosition runs.
  const [positionForAnchor, setPositionForAnchor] = useState<Element | null>(null);
  const positionReady = positionForAnchor === anchorEl;
  const [modalRef, setModalRef] = useState<HTMLDivElement | null>(null);
  const [spotlightRect, setSpotlightRect] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
    borderRadius?: number;
  }>(noAnchorSpotlightRect);

  const calculatePosition = useCallback(() => {
    if (!modalRef) return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const modalRect = modalRef.getBoundingClientRect();
    const modalWidth = modalRect.width || 0;
    const modalHeight = modalRect.height || 0;
    const viewportPadding = 32;
    const defaultSpacing = 20;

    let x = 0;
    let y = 0;

    if (anchorEl) {
      const positionType = positionConfig?.type || "bottom-center";
      const rect = anchorEl.getBoundingClientRect();

      // Calculate base position based on position type
      switch (positionType) {
        case "top-left":
          x = rect.left;
          y = rect.top - modalHeight - defaultSpacing;
          break;
        case "top-center":
          x = rect.left + rect.width / 2 - modalWidth / 2;
          y = rect.top - modalHeight - defaultSpacing;
          break;
        case "top-right":
          x = rect.right - modalWidth;
          y = rect.top - modalHeight - defaultSpacing;
          break;
        case "bottom-left":
          x = rect.left;
          y = rect.bottom + defaultSpacing;
          break;
        case "bottom-center":
          x = rect.left + rect.width / 2 - modalWidth / 2;
          y = rect.bottom + defaultSpacing;
          break;
        case "bottom-right":
          x = rect.right - modalWidth;
          y = rect.bottom + defaultSpacing;
          break;
        case "left-top":
          x = rect.left - modalWidth - defaultSpacing;
          y = rect.top;
          break;
        case "left-center":
          x = rect.left - modalWidth - defaultSpacing;
          y = rect.top + rect.height / 2 - modalHeight / 2;
          break;
        case "left-bottom":
          x = rect.left - modalWidth - defaultSpacing;
          y = rect.bottom - modalHeight;
          break;
        case "right-top":
          x = rect.right + defaultSpacing;
          y = rect.top;
          break;
        case "right-center":
          x = rect.right + defaultSpacing;
          y = rect.top + rect.height / 2 - modalHeight / 2;
          break;
        case "right-bottom":
          x = rect.right + defaultSpacing;
          y = rect.bottom - modalHeight;
          break;
        case "center":
          x = rect.left + rect.width / 2 - modalWidth / 2;
          y = rect.top + rect.height / 2 - modalHeight / 2;
          break;
      }

      // Apply offsets only when there's an anchor
      const xOffset = positionConfig?.xOffset || 0;
      const yOffset = positionConfig?.yOffset || 0;
      x += xOffset;
      y += yOffset;
    } else {
      // Center on screen when no anchor - ignore position config
      x = viewportWidth / 2 - modalWidth / 2;
      y = viewportHeight / 2 - modalHeight / 2;
    }

    // Constrain to viewport bounds
    x = Math.max(viewportPadding, Math.min(x, viewportWidth - modalWidth - viewportPadding));
    y = Math.max(viewportPadding, Math.min(y, viewportHeight - modalHeight - viewportPadding));

    setPosition({ x, y });
    setPositionForAnchor(anchorEl);

    // Calculate spotlight rect
    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      const padding = 12; // px around the element

      setSpotlightRect({
        left: rect.left - padding,
        top: rect.top - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
        borderRadius: 12,
      });
    } else {
      setSpotlightRect(noAnchorSpotlightRect);
    }
  }, [modalRef, anchorEl, positionConfig]);

  useEffect(() => {
    calculatePosition();
  }, [calculatePosition]);

  useEffect(() => {
    const handleResize = () => {
      calculatePosition();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [calculatePosition]);

  // Observe modal size changes for dynamic content
  useEffect(() => {
    if (!modalRef) return;

    const resizeObserver = new ResizeObserver(() => {
      calculatePosition();
    });

    resizeObserver.observe(modalRef);
    return () => resizeObserver.disconnect();
  }, [modalRef, calculatePosition]);

  return (
    <div className="fixed inset-0">
      {spotlightRect ? (
        <div
          className="absolute"
          style={{
            left: spotlightRect.left,
            top: spotlightRect.top,
            width: spotlightRect.width,
            height: spotlightRect.height,
            borderRadius: spotlightRect.borderRadius || 0,
            boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.5)`,
          }}
        />
      ) : (
        <div className="absolute top-0 left-0 w-full h-full bg-black/50" />
      )}

      {anchorEl ? (
        <motion.div
          ref={setModalRef}
          className={cn(
            "flex rounded-lg flex-col items-center justify-center z-50 md:max-w-[550px] max-w-[90vw] border relative",
            "bg-gray-600 border-gray-500 p-3",
            className,
          )}
          initial={{ opacity: 0, y: 8 }}
          animate={{
            opacity: positionReady && !isTransitioning ? 1 : 0,
            y: positionReady && !isTransitioning ? 0 : 8,
          }}
          transition={{
            duration: positionReady && !isTransitioning ? 0.3 : 0,
            ease: "easeOut",
          }}
          style={{
            position: "absolute",
            left: position.x,
            top: position.y,
          }}
        >
          {positionConfig?.type && (
            <div
              className={cn("absolute", getCaretStyles(positionConfig.type).className)}
              style={{
                transform: `rotate(${getCaretStyles(positionConfig.type).rotation}deg)`,
              }}
            >
              <TailSvg />
            </div>
          )}
          <Button
            variant="blank"
            size="sm"
            onClick={closeTour}
            className="absolute top-2 right-2 z-50 rounded-full hover:bg-gray-500"
            data-statsig-name="nux-tour-close"
          >
            <X className="size-4 text-primary-foreground" />
          </Button>
          {children}
        </motion.div>
      ) : (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <motion.div
            ref={setModalRef}
            className={cn(
              "flex rounded-lg flex-col items-center justify-start w-[min(90vw,550px)] border relative",
              // A step taller than the window scrolls inside the card rather
              // than running its last line, and its button, off the screen.
              "max-h-[calc(100dvh-2rem)] overflow-y-auto",
              "bg-white border-gray-200 pt-10 px-8 pb-7",
              className,
            )}
            initial={false}
            animate={{
              opacity: !isTransitioning ? 1 : 0,
              y: !isTransitioning ? 0 : 8,
            }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <Button
              variant="ghostPopover"
              size="sm"
              onClick={closeTour}
              className="absolute top-2 right-2 z-50"
              data-statsig-name="nux-tour-close"
            >
              <X className="size-4 text-black" />
            </Button>
            {children}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export const useTourManager = ({ steps }: UseTourManagerProps) => {
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(-1);
  const [portalContainer, setPortalContainer] = useState<Element | null>(null);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [, setSearchParams] = useSearchParams();
  const { logEvent } = useStatsigLogger();

  const activeStep = currentStepIndex >= 0 ? steps[currentStepIndex] : undefined;

  useEffect(() => {
    if (currentStepIndex < 0) {
      setPortalContainer(null);
    } else {
      setPortalContainer(document.body);
    }
  }, [currentStepIndex]);

  useEffect(() => {
    if (activeStep) {
      setIsTransitioning(true);

      // Brief delay lets React flush the transitioning state before we update anchor + reveal.
      // The positionReady guard prevents the dialog from rendering until position is calculated.
      const timer = setTimeout(() => {
        if (activeStep.anchor) {
          // Find the visible instance when multiple elements share an anchor id
          // (e.g. responsive layouts that render a component at different breakpoints).
          // Try data-nux-anchor first (supports multiple responsive instances),
          // then fall back to id for existing anchors.
          const candidates = document.querySelectorAll(
            `[data-nux-anchor="${activeStep.anchor}"], #${activeStep.anchor}`,
          );
          const visible = Array.from(candidates).find((el) => {
            const { width, height } = el.getBoundingClientRect();
            return width > 0 && height > 0;
          });
          setAnchorEl(visible ?? null);
        } else {
          setAnchorEl(null);
        }
        setIsTransitioning(false);
      }, 50);

      return () => clearTimeout(timer);
    } else {
      setAnchorEl(null);
      setIsTransitioning(false);
    }
  }, [activeStep]);

  const startTour = useCallback(() => {
    setIsTransitioning(true);
    setCurrentStepIndex(0);
    logEvent("step_view", undefined, {
      step: `nux_${steps[0]?.id}`,
      step_index: "0",
      total_steps: steps.length.toString(),
      is_start: "true",
    });
  }, [logEvent, steps]);

  const closeTour = useCallback(() => {
    setSearchParams(withoutNuxParam, { replace: true });
    setCurrentStepIndex(-1);
  }, [setSearchParams]);

  const goToNextStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      const nextStep = steps[currentStepIndex + 1];
      logEvent("step_view", undefined, {
        step: `nux_${nextStep?.id}`,
        step_index: (currentStepIndex + 1).toString(),
        total_steps: steps.length.toString(),
      });
      setIsTransitioning(true);
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      // Tour completed
      logEvent("step_view", undefined, {
        step: "nux_completed",
        total_steps: steps.length.toString(),
      });
      // Drop `?nux=true` so a reload doesn't replay a tour the user finished.
      setSearchParams(withoutNuxParam, { replace: true });
      setCurrentStepIndex(-1);
    }
  }, [currentStepIndex, steps, logEvent, setSearchParams]);

  const goToPreviousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setIsTransitioning(true);
      const prevStep = steps[currentStepIndex - 1];
      logEvent("step_view", undefined, {
        step: `nux_${prevStep?.id}`,
        step_index: (currentStepIndex - 1).toString(),
        total_steps: steps.length.toString(),
      });
      setCurrentStepIndex(currentStepIndex - 1);
    }
  }, [currentStepIndex, steps, logEvent]);

  const renderPortal = useCallback(() => {
    if (!portalContainer || currentStepIndex < 0) return null;

    return createPortal(
      <TourStepWrapper
        anchorEl={anchorEl}
        positionConfig={activeStep?.position}
        closeTour={closeTour}
        isTransitioning={isTransitioning}
      >
        {activeStep && (
          <motion.div
            key={activeStep.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: isTransitioning ? 0 : 1 }}
            transition={{ duration: isTransitioning ? 0 : 0.3 }}
          >
            <activeStep.Component
              onNext={goToNextStep}
              onPrevious={goToPreviousStep}
              closeTour={closeTour}
              anchor={activeStep.anchor}
            />
          </motion.div>
        )}
      </TourStepWrapper>,
      portalContainer,
    );
  }, [
    activeStep,
    portalContainer,
    isTransitioning,
    anchorEl,
    goToNextStep,
    goToPreviousStep,
    currentStepIndex,
    closeTour,
  ]);

  const tourState = useMemo(
    () => ({
      currentStepIndex,
      totalSteps: steps.length,
      isFirstStep: currentStepIndex === 0,
      isLastStep: currentStepIndex === steps.length - 1,
    }),
    [currentStepIndex, steps.length],
  );

  return {
    startTour,
    closeTour,
    renderPortal,
    ...tourState,
  };
};
